import { DepressionContentPage } from "@/components/DepressionContentPage";
import type { FAQItem } from "@/components/DepressionContentPage";

const faqs: FAQItem[] = [
  {
    question: "Can someone develop depression without having it before?",
    answer:
      "Yes. Depressive episodes can occur in people without any previous diagnosed episode. A history of depression is not required for someone to experience it now.",
  },
  {
    question: "What are early signs of depression?",
    answer:
      "Early signs may include sleep changes, anxiety, loss of interest, fatigue, appetite changes, difficulty concentrating, social withdrawal, or feeling unlike yourself. Not everyone experiences sadness first.",
  },
  {
    question: "Can anxiety appear before depression?",
    answer:
      "Yes. Anxiety, panic, and sleep disruption can sometimes precede or accompany depressive symptoms. The relationship varies from person to person.",
  },
  {
    question: "Can a panic attack be followed by ongoing anxiety?",
    answer:
      "A frightening panic episode can be followed by ongoing worry, hypervigilance, and fear of another attack. When this persists, an evaluation can help clarify what is happening.",
  },
  {
    question: "Can sleep problems occur with depression?",
    answer:
      "Yes. Sleeping much more or much less than usual can accompany depression, as can difficulty falling or staying asleep.",
  },
  {
    question: "How is depression evaluated?",
    answer:
      "A clinician reviews symptoms, onset, sleep, anxiety, mood, treatment history, medications, medical history, and safety to understand the full picture before discussing options.",
  },
  {
    question: "When is TMS considered?",
    answer:
      "TMS may be considered for eligible patients after clinical evaluation, often when depression has not responded adequately to prior treatment. Candidacy is determined individually.",
  },
];

export default function SuddenDepression() {
  return (
    <DepressionContentPage
      seo={{
        title: "I Never Had Depression Before. Why Now?",
        description:
          "Depression can appear even without a previous history. Learn why evaluation matters when mood, sleep, anxiety or functioning suddenly change.",
        canonical: "/sudden-depression",
        keywords:
          "sudden depression, first episode of depression, new depression symptoms, anxiety before depression, mental health evaluation",
      }}
      badge="Mental Health Education"
      heroTitle="I Never Had Depression Before. Why Is This Happening Now?"
      heroSubtitle="A major change in mood, anxiety, sleep or everyday functioning can feel especially frightening when you have never experienced anything like it before."
      heroCtaPrimary="Check My Insurance Options"
      heroCtaPrimaryHref="/check-my-insurance-options"
      sections={[
        {
          heading: "Depression Does Not Always Follow a Lifelong Pattern",
          variant: "text",
          body: (
            <p>
              Many people assume depression is something someone has always
              lived with. But depressive episodes can occur in people without
              any previous diagnosed episode. A change in mood, sleep, or
              functioning can appear at any point in life, and it does not mean
              something is fundamentally wrong with you.
            </p>
          ),
        },
        {
          heading: "Sometimes the First Sign Isn't Sadness",
          variant: "cards",
          cards: [
            {
              title: "Sleep Changes",
              body: "Sleeping much more or much less than usual.",
            },
            {
              title: "Anxiety",
              body: "Persistent worry or a sense of unease that wasn't there before.",
            },
            {
              title: "Panic",
              body: "A frightening panic episode that seems to come out of nowhere.",
            },
            {
              title: "Loss of Interest",
              body: "No longer enjoying activities that used to matter.",
            },
            {
              title: "Fatigue",
              body: "Feeling exhausted even when rest should help.",
            },
            {
              title: "Appetite Changes",
              body: "Eating much more or much less than usual.",
            },
            {
              title: "Difficulty Concentrating",
              body: "Trouble focusing or following through on tasks.",
            },
            {
              title: "Social Withdrawal",
              body: "Pulling back from friends, family, or routines.",
            },
            {
              title: "Feeling Unlike Yourself",
              body: "A sense that something has shifted, even if you can't name it.",
            },
          ],
        },
        {
          heading: "Look for What Changed",
          variant: "text",
          body: (
            <>
              <p>
                When symptoms appear suddenly, it can help to notice what
                changed around the same time. Clinicians may consider recent
                stress, sleep disruption, panic episodes, trauma or triggering
                experiences, medical issues, medication or substance factors,
                and major life changes.
              </p>
              <p>
                This is not an invitation to self-diagnose the cause. It is
                information that can help a clinician understand the full
                picture during an evaluation.
              </p>
            </>
          ),
        },
        {
          heading: "Why Clinicians Look at the Whole Picture",
          variant: "text",
          body: (
            <>
              <p>
                Symptoms can overlap across several conditions. An evaluation
                helps sort out what may be contributing, which may include:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Depression</li>
                <li>Anxiety</li>
                <li>Trauma</li>
                <li>Bipolar symptoms</li>
                <li>Substance use</li>
                <li>Medical causes</li>
                <li>Medication effects</li>
                <li>Sleep disorders</li>
              </ul>
              <p>
                The goal is to understand the pattern before deciding which
                treatment options may be appropriate.
              </p>
            </>
          ),
        },
        {
          heading:
            "You Don't Need to Know the Diagnosis Before Asking for Help",
          variant: "text",
          body: (
            <p>
              You do not need a diagnosis to start a conversation. A
              consultation can help organize what you are experiencing and point
              toward appropriate next steps.
            </p>
          ),
        },
      ]}
      faq={faqs}
      faqHeading="Frequently Asked Questions"
      relatedLinks={[
        {
          title: "Depression Treatment Options",
          href: "/depression-treatment-options",
        },
        {
          title: "When Anxiety, Insomnia and Depression Overlap",
          href: "/depression-anxiety-insomnia",
        },
        {
          title: "Am I a Candidate for TMS?",
          href: "/am-i-a-candidate",
        },
        {
          title: "Explore More Mental Health Education",
          href: "/mental-health-resources",
        },
      ]}
      finalCtaHeading="You Don't Need to Have It All Figured Out First"
      finalCtaText="If something has changed and you're not sure why, a clinical evaluation can help you understand what's happening and what options may be appropriate."
      finalCtaPrimaryLabel="Check My Insurance Options"
      finalCtaPrimaryHref="/check-my-insurance-options"
      finalCtaSecondaryLabel="Schedule a Private Consultation"
      finalCtaSecondaryHref="/contactus"
    />
  );
}
